import Head from 'next/head';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEffectOnce, useMedia } from 'react-use';
import {
    FreightPriceModal,
    ProductInfo,
    ProductMedia,
    ProductNotification,
    ProductPrice,
    ProductStatusInfo,
} from '~/features/ProductDetail';
import EUGaranModal from '~/features/ProductDetail/components/EUGaranModal/EUGaranModal';
import HazardSymbols from '~/features/ProductDetail/components/HazardSymbols';
import {
    ProductDescription,
    ProductFAQ,
    SustainabilityValuesDescription,
} from '~/features/ProductDetail/components/ProductAccordions';
import HazardAccordion from '~/features/ProductDetail/components/ProductAccordions/Hazard';
import ProductDeliveryAccordion from '~/features/ProductDetail/components/ProductAccordions/ProductDelivery';
import ProductDocumentAccordion from '~/features/ProductDetail/components/ProductAccordions/ProductDocuments';
import { ProductGalleryModal } from '~/features/ProductDetail/components/ProductGalleryModal/ProductGalleryModal';
import { ShopTypes, UmbracoTypes } from '~/lib/data-contract';
import { useCreateEvents } from '~/services/loop54/hooks';
import {
    useFreight,
    usePrice,
    useProduct,
    useProductImages,
    useStockDetails,
    useUserProfile,
} from '~/services/shop/hooks';
import { useFrame, usePage, useTranslations } from '~/services/umbraco/hooks';

import { useSession } from 'next-auth/react';
import { AccordionItemRef } from '~/shared/components/Accordion';
import { EnergyLabel } from '~/shared/components/EnergyLabel';
import { EUGaranLabel } from '~/shared/components/EUGaranLabel';
import { Gutter } from '~/shared/components/Gutter';
import { MaxWidth } from '~/shared/components/MaxWidth';
import { Modal } from '~/shared/components/Modal';
import { ProductSpecifications } from '~/shared/components/ProductSpecifications';
import { USP } from '~/shared/components/USP';
import { useConfig, useScrollTo } from '~/shared/hooks';
import { Schema, SchemaTypes } from '~/shared/schema';
import gtm from '~/shared/tracking/gtm';
import { useContextGroup } from '~/shared/utils/cookie/hooks';
import { capitalizeWholeWords, getDifferenceInCalendarDays } from '~/shared/utils/helpers';
import { useFreightType, usePostalCode } from '~/shared/utils/storage';
import { setActiveSetWarehouseId, useModalFreight } from '~/state/useModalFreight';
import useModalState from '~/state/useModalState';
import useQuickModalState from '~/state/useQuickAddToBasketState';
import DynamicBlocks from '~/templates/blocks';
import {
    M30ComplementaryProductCarousel,
    M30RecentlyViewedProductCarousel,
    M30RelatedProductCarousel,
} from '~/templates/blocks/components/M30ProductCarousel';
import { queries, theme } from '~/theme';
import AddToBasket from './components/AddToBasket';
import { VariantSelector } from './components/VariantSelector';
import {
    BottomProductDataWrapper,
    CampaignNotificationWrapper,
    EnergyLabelWrapper,
    EUGaranLabelWrapper,
} from './components/styled';
import {
    AccordionWrapper,
    BetaBanner,
    GridArea,
    Images,
    Information,
    ProductData,
    Wrapper,
} from './styled';
import { prefetchVariants } from './utils/prefetchVariants';
import useCanUserAccessFunctionality from '~/shared/utils/mit-fog/canUserAccessFunctionality';
import { SkeletonLoader } from '~/shared/components/SkeletonLoader';

export default function P50ProductDetailPage() {
    const { data, status } = useSession();
    const { mutate: onProductClick } = useCreateEvents();
    const { MAX_DELIVERY_DAYS_DIFFERENCE } = useConfig();
    const { translate } = useTranslations();
    const { data: brandUrl } = useFrame((frame) => frame.staticLinks?.brandPage?.url);
    const { data: page } = usePage<UmbracoTypes.IP50ProductDetailPage>();
    const itemNo = decodeURIComponent(page?.pageProperties?.productItemNumber || '');
    const { data: product, isLoading: productIsLoading } = useProduct(
        itemNo,
        ({ product }) => product,
        (product) => prefetchVariants(product, itemNo),
    );
    const {
        freightPriceSuccessCallback,
        freightPriceModalId,
        setFreightPriceSuccessCallback,
        setFreightPriceModalId,
    } = useModalState();

    const { data: profile } = useUserProfile();
    const token = data?.accessToken ?? null;
    const customerId = profile?.customer?.id ?? '';

    const isDesktop = useMedia(queries.md, false);
    const [imageModalIndex, setImageModalIndex] = useState(-1);
    const [isEUGaranModalOpen, setIsEUGaranModalOpen] = useState(false);

    const { activeWarehouseId } = useModalFreight();

    // Store context group in cookie to change the frontpage content
    const [contextGroup, setContextGroup] = useContextGroup();

    useEffect(() => {
        if (product?.contextGroupKey && contextGroup !== product?.contextGroupKey) {
            setContextGroup(product?.contextGroupKey);
        }
    }, [product?.contextGroupKey, contextGroup, setContextGroup]);

    const descriptionRef = useRef<AccordionItemRef>(null);
    const hazardSymbolsRef = useRef<AccordionItemRef>(null);
    const scrollToDescription = useScrollTo(descriptionRef);
    const scrollToHazardSymbols = useScrollTo(hazardSymbolsRef);

    const [hasComplementaryProducts, setHasComplementaryProducts] = useState(false);
    const complementaryProductsRef = useRef<HTMLDivElement>(null);
    const scrollToComplementaryProducts = useScrollTo(complementaryProductsRef);

    const productIsObsolete = product?.item?.itemStatus === ShopTypes.ItemStatusValues.Obsolete;

    const {
        data: pricesData,
        isLoading: pricesIsLoading,
        isError: pricesIsError,
    } = usePrice(itemNo, !productIsObsolete);

    const isUser = useCanUserAccessFunctionality();
    const showPrice =
        status === 'unauthenticated' ||
        (status === 'authenticated' && !!isUser?.shouldDisplayPrices);

    const [postalCode] = usePostalCode();

    const { data: frame } = useFrame();
    const { data: freight } = useFreight({
        postalCode,
        itemNo,
    });

    const { data: stockDetails } = useStockDetails({
        enabled: !productIsObsolete,
        itemNo,
        postalCode,
    });

    const [freightType] = useFreightType();

    const { data: images } = useProductImages();

    const calculatedDeliveryDate =
        freightType && stockDetails?.[freightType]?.calculatedDeliveryDate;

    const productCannotShipInTime = calculatedDeliveryDate
        ? getDifferenceInCalendarDays(calculatedDeliveryDate) > MAX_DELIVERY_DAYS_DIFFERENCE
        : false;

    const productIsOutOfStock =
        stockDetails?.online?.status === ShopTypes.StockStatus.OutOfStock &&
        stockDetails?.pickup?.status === ShopTypes.StockStatus.OutOfStock;

    const displayPrice = pricesData;
    const info = product?.item ? product.item : product;
    const name = `${info?.name ? `${info?.name} ` : ''}${info?.name2 ?? ''}`;
    const metaTitle = capitalizeWholeWords(name, true);

    const variantOptions = useMemo(
        () =>
            product?.variants?.filter(Boolean)?.map((option) => ({
                title: option.variantName,
                value: option.itemNumber,
                url: option.itemUrlSegment,
            })) || [],
        [product?.variants],
    );

    // Prefetch variants on first load to avoid delay when the user clicks on the variant selector
    useEffectOnce(() => {
        if (!itemNo || !product?.variants?.length) {
            return;
        }

        prefetchVariants(product, itemNo);
    });

    useEffect(() => {
        if (product) {
            onProductClick({
                type: 'click',
                entity: {
                    type: 'Product',
                    id: String(product.item?.itemNumber || product.primaryItemNumber),
                },
            });
        }
    }, [product, onProductClick]);

    useEffect(() => {
        setActiveSetWarehouseId(undefined);
    }, [itemNo]);

    const { itemList } = useQuickModalState((state) => ({
        itemList: state.itemList,
    }));

    useEffect(() => {
        if (product && !productIsLoading && displayPrice && !pricesIsLoading) {
            gtm.ecommerce.viewItem(
                product,
                displayPrice?.price,
                displayPrice?.activeCampaigns,
                itemList,
            );
        }
    }, [product, displayPrice, productIsLoading, pricesIsLoading, itemList]);

    useEffect(() => {
        if (
            !(
                productIsObsolete ||
                pricesIsLoading ||
                status === 'loading' ||
                (status === 'authenticated' && !showPrice) ||
                (showPrice && !pricesIsError)
            ) &&
            pricesIsError
        ) {
            gtm.error.getPricesCall(product);
        }
    }, [
        displayPrice?.price,
        pricesIsError,
        pricesIsLoading,
        product,
        productIsObsolete,
        showPrice,
        status,
    ]);

    const pluralPriceString = useMemo(() => {
        if (!displayPrice?.price?.pluralPrices || !displayPrice.price.pluralPrices.length) {
            return null;
        }

        const pluralPriceInfo = displayPrice.price.pluralPrices[0].price;
        const pluralPriceQuantity = displayPrice.price.pluralPrices[0].quantity;
        const pluralPrice = (
            (status === 'authenticated' && !!isUser?.shouldDisplayPricesExVat
                ? pluralPriceInfo.priceExVat
                : pluralPriceInfo.priceInclVat) * pluralPriceQuantity
        )
            .toFixed(2)
            .replace('.', ',');

        return translate('generic.pluralPriceSplash')
            .replace('[QUANTITY]', pluralPriceQuantity.toString())
            .replace('[SALES_UNIT]', product?.salesUnitFormatted || '')
            .replace('[PRICE]', pluralPrice);
    }, [
        displayPrice?.price?.pluralPrices,
        isUser?.shouldDisplayPricesExVat,
        product?.salesUnitFormatted,
        status,
        translate,
    ]);

    return (
        <>
            <MaxWidth>
                <Head>
                    <title key="title">{metaTitle}</title>
                    {!page?.meta?.description && (
                        <meta
                            name="description"
                            key="description"
                            content={(
                                product?.shortDescription +
                                ' ' +
                                product?.longDescription
                            )?.replaceAll('da:,pl:', '')}
                        />
                    )}
                </Head>
                <Gutter>
                    <Wrapper>
                        <Images>
                            <ProductMedia
                                itemNo={itemNo}
                                onClick={(index) => {
                                    if (isDesktop) {
                                        setImageModalIndex(index);
                                    }
                                }}
                            />
                        </Images>

                        <Information>
                            <ProductData>
                                <GridArea area="header">
                                    {productIsLoading ? (
                                        <ProductInfo.Skeleton />
                                    ) : !product ? (
                                        <ProductNotification
                                            content={translate('product.error.fetchingData')}
                                            variant="high"
                                        />
                                    ) : (
                                        <ProductInfo
                                            brandUrl={brandUrl}
                                            product={product}
                                            campaigns={displayPrice?.activeCampaigns}
                                            pluralPrices={pluralPriceString}
                                            onReadMoreClick={async (event: React.MouseEvent) => {
                                                event.preventDefault();
                                                descriptionRef.current?.open();
                                                scrollToDescription();
                                            }}
                                            onComplementaryProductsClick={async (
                                                event: React.MouseEvent,
                                            ) => {
                                                event.preventDefault();
                                                scrollToComplementaryProducts();
                                            }}
                                            hasComplementaryProducts={hasComplementaryProducts}
                                        />
                                    )}
                                </GridArea>

                                <GridArea area="variants">
                                    {variantOptions.length < 2 || productIsObsolete ? null : (
                                        <VariantSelector
                                            label={product?.item?.variantType}
                                            selectedVariantValue={product?.item?.itemNumber}
                                            variantList={variantOptions}
                                        />
                                    )}
                                </GridArea>

                                <GridArea area="price">
                                    {status === 'authenticated' ? (
                                        <BetaBanner
                                            variant="high"
                                            content={translate('mit-fog.betabanner')}
                                        />
                                    ) : null}

                                    {displayPrice?.activeCampaigns?.map((campaign, index) =>
                                        campaign?.title || campaign?.text || campaign?.url ? (
                                            <CampaignNotificationWrapper
                                                key={`${campaign.entityId}|${index}`}
                                            >
                                                <ProductNotification
                                                    backgroundColor={theme.colors.grey30}
                                                    borderColor={theme.colors.grey40}
                                                    content={campaign.text}
                                                    link={{
                                                        url: campaign.url || '',
                                                        text: campaign.linkText,
                                                        target: campaign?.url?.startsWith('/')
                                                            ? '_self'
                                                            : '_blank',
                                                    }}
                                                    textColor={theme.colors.brandBlue}
                                                    title={campaign.title}
                                                    variant="campaign"
                                                />
                                            </CampaignNotificationWrapper>
                                        ) : null,
                                    )}

                                    {productIsObsolete ? null : pricesIsLoading ||
                                      status === 'loading' ? (
                                        <ProductPrice.Skeleton />
                                    ) : status === 'authenticated' && !showPrice ? (
                                        <ProductNotification
                                            variant="high"
                                            content={translate('mit-fog.missingPermissions')}
                                        />
                                    ) : showPrice && !pricesIsError ? (
                                        <>
                                            <ProductPrice
                                                priceItem={displayPrice}
                                                product={product}
                                                displayPriceExVat={
                                                    status === 'authenticated' &&
                                                    !!isUser?.shouldDisplayPricesExVat
                                                }
                                                showOriginalPrice={!customerId && !token}
                                            />
                                        </>
                                    ) : pricesIsError ? (
                                        <ProductNotification
                                            content={translate('product.error.fetchingPrice')}
                                            variant="high"
                                        />
                                    ) : null}
                                </GridArea>
                                {status === 'loading' ? (
                                    <>
                                        <GridArea area="buy">
                                            <SkeletonLoader height={100} isFullWidth />
                                        </GridArea>
                                        <GridArea area="stock">
                                            <SkeletonLoader
                                                height={isDesktop ? 40 : 35}
                                                isFullWidth
                                            />
                                        </GridArea>
                                    </>
                                ) : status === 'authenticated' ? (
                                    productIsObsolete ? (
                                        <GridArea area="buy">
                                            <ProductNotification
                                                variant="high"
                                                content={translate('product.obsolete')}
                                            />
                                        </GridArea>
                                    ) : null
                                ) : (
                                    <>
                                        <GridArea area="buy">
                                            {productIsObsolete ? (
                                                <ProductNotification
                                                    variant="high"
                                                    content={translate('product.obsolete')}
                                                />
                                            ) : productIsOutOfStock ? (
                                                <ProductNotification
                                                    variant="high"
                                                    content={translate('product.soldOut')}
                                                />
                                            ) : productCannotShipInTime ? (
                                                <ProductNotification
                                                    variant="high"
                                                    content={translate(
                                                        'product.delivery.time.exceeded',
                                                    )}
                                                />
                                            ) : (
                                                <AddToBasket
                                                    itemNo={itemNo}
                                                    activeWarehouseId={activeWarehouseId}
                                                    callbackHandler={setFreightPriceSuccessCallback}
                                                    freightPriceHandler={setFreightPriceModalId}
                                                    shouldShowPowerStep
                                                    modalType={'freight'}
                                                    productStatus={product?.item?.itemStatus}
                                                    productIsOutOfStock={productIsOutOfStock}
                                                />
                                            )}
                                        </GridArea>
                                    </>
                                )}

                                <GridArea area="stock" style={{ marginTop: 10 }}>
                                    <ProductStatusInfo
                                        modalType={'freight'}
                                        itemNo={itemNo}
                                        activeWarehouseId={activeWarehouseId}
                                        productStatus={product?.item?.itemStatus}
                                        setFreightHandler={setFreightPriceModalId}
                                        productIsOutOfStock={productIsOutOfStock}
                                        canCreateOrder={status === 'unauthenticated' ? true : false}
                                    />
                                </GridArea>

                                <GridArea area="info">
                                    <USP />

                                    <BottomProductDataWrapper>
                                        {!product?.item?.energyLabel ||
                                        !product?.item?.energyLabelLink ||
                                        !product?.item?.energyProductDataSheetLink ? null : (
                                            <EnergyLabelWrapper>
                                                <EnergyLabel
                                                    variant="pdp"
                                                    energyClass={product?.item?.energyLabel}
                                                    energyLabelLink={product?.item?.energyLabelLink}
                                                    energyProductDataSheetLink={
                                                        product?.item?.energyProductDataSheetLink
                                                    }
                                                />
                                            </EnergyLabelWrapper>
                                        )}

                                        {!product?.item?.euGaranEnabled ||
                                        !product?.item?.euGaranYears ? null : (
                                            <EUGaranLabelWrapper>
                                                <EUGaranLabel
                                                    euGaranYears={product.item.euGaranYears}
                                                    onClick={() => setIsEUGaranModalOpen(true)}
                                                    rightsLink={frame?.staticLinks?.statutoryRightsPage}
                                                />
                                            </EUGaranLabelWrapper>
                                        )}

                                        {product?.item ? (
                                            <HazardSymbols
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    hazardSymbolsRef.current?.open();
                                                    scrollToHazardSymbols();
                                                }}
                                                product={product.item}
                                            />
                                        ) : null}
                                    </BottomProductDataWrapper>
                                </GridArea>
                            </ProductData>
                        </Information>
                    </Wrapper>
                    <AccordionWrapper>
                        <ProductDescription ref={descriptionRef} product={product} />

                        <ProductSpecifications product={product} />

                        <SustainabilityValuesDescription
                            sustainabilityValues={product?.item?.sustainabiltyValues}
                        />

                        <ProductDeliveryAccordion product={product} />

                        <HazardAccordion ref={hazardSymbolsRef} product={product} />

                        <ProductDocumentAccordion product={product} />

                        <ProductFAQ product={product} />
                    </AccordionWrapper>
                </Gutter>
            </MaxWidth>

            <M30ComplementaryProductCarousel
                inputRef={complementaryProductsRef}
                productId={itemNo}
                headline={translate('product.complementaryProduct.headline')}
                id={translate('product.complementaryProduct.headline')}
                setHasComplementaryProducts={setHasComplementaryProducts}
            />

            <M30RelatedProductCarousel
                productId={itemNo}
                headline={translate('product.relatedProduct.headline')}
                id={translate('product.relatedProduct.headline')}
            />

            <M30RecentlyViewedProductCarousel
                headline={translate('product-list.recently-viewed-products')}
                id={translate('product-list.recently-viewed-products')}
            />
            <DynamicBlocks elements={page?.pageElements as UmbracoTypes.IModules[]} />

            <Modal
                isOpen={imageModalIndex > -1}
                handleClose={() => setImageModalIndex(-1)}
                type="fullscreen"
            >
                <ProductGalleryModal
                    imageIndex={imageModalIndex}
                    setImageIndex={setImageModalIndex}
                />
            </Modal>

            <Modal
                isOpen={isEUGaranModalOpen && Boolean(product?.item?.itemNumber)}
                handleClose={() => setIsEUGaranModalOpen(false)}
            >
                <EUGaranModal
                    itemNo={product?.item?.itemNumber ?? ''}
                />
            </Modal>

            <Modal
                isOpen={Boolean(freightPriceModalId)}
                handleClose={() => {
                    setFreightPriceModalId();
                    setFreightPriceSuccessCallback();
                }}
            >
                <FreightPriceModal
                    itemNo={freightPriceModalId}
                    onSuccess={(postalCode) => {
                        setFreightPriceModalId();
                        freightPriceSuccessCallback?.(postalCode);
                    }}
                />
            </Modal>
            <Schema
                type={SchemaTypes.Product}
                props={{
                    brandUrl: product?.brand
                        ? frame?.staticLinks?.brandPage?.url + '/' + product?.brand
                        : '',
                    category: /[^|]*$/.exec(product?.categoryBreadcrumb || '')?.[0],
                    freight,
                    product,
                    price: displayPrice?.price,
                    image: images?.[0],
                    stockDetails,
                }}
            />
        </>
    );
}
